import { useEffect, useMemo, useState, type ReactNode } from "react";
import type { User } from "@supabase/supabase-js";
import { supabase } from "@/lib/supabase/client";
import type { TableRow } from "@/types/database";
import { TenantContext, type TenantContextValue } from "@/features/auth/tenant-context";

export type TenantMembership = TableRow<"tenant_members">;
export type Tenant = TableRow<"tenants">;

const ACTIVE_TENANT_KEY = "shipment-intelligence.active-tenant";

async function loadTenantContext(user: User) {
  const [membershipResult, profileResult] = await Promise.all([
    supabase.from("tenant_members").select("id, tenant_id, user_id, role, created_at").eq("user_id", user.id).order("created_at"),
    supabase.from("profiles").select("full_name").eq("id", user.id).maybeSingle(),
  ]);

  if (membershipResult.error) throw membershipResult.error;
  if (profileResult.error) throw profileResult.error;

  const memberships = membershipResult.data ?? [];
  const tenantIds = memberships.map((membership) => membership.tenant_id);
  const tenantsResult = tenantIds.length
    ? await supabase.from("tenants").select("id, name, slug, created_at, updated_at").in("id", tenantIds)
    : { data: [], error: null };

  if (tenantsResult.error) throw tenantsResult.error;
  return { memberships, tenants: tenantsResult.data ?? [], profileName: profileResult.data?.full_name ?? null };
}

export function TenantProvider({ user, children }: { user: User; children: ReactNode }) {
  const [memberships, setMemberships] = useState<TenantMembership[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [activeTenantId, setActiveTenantId] = useState<string | null>(null);
  const [profileName, setProfileName] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setIsLoading(true);
    setError(null);
    void loadTenantContext(user)
      .then((result) => {
        if (!active) return;
        setMemberships(result.memberships);
        setTenants(result.tenants);
        setProfileName(result.profileName);
        const stored = window.localStorage.getItem(ACTIVE_TENANT_KEY);
        const storedIsValid = result.tenants.some((tenant) => tenant.id === stored);
        setActiveTenantId(storedIsValid ? stored : result.tenants[0]?.id ?? null);
      })
      .catch((loadError: unknown) => {
        if (!active) return;
        setError(loadError instanceof Error ? loadError.message : "Unable to load tenant context.");
      })
      .finally(() => {
        if (active) setIsLoading(false);
      });

    return () => {
      active = false;
    };
  }, [user]);

  const activeTenant = tenants.find((tenant) => tenant.id === activeTenantId) ?? null;
  const activeMembership = memberships.find((membership) => membership.tenant_id === activeTenantId) ?? null;

  const value = useMemo<TenantContextValue>(
    () => ({
      memberships,
      tenants,
      activeTenant,
      activeMembership,
      isLoading,
      error,
      profileName,
      setActiveTenant: (tenantId) => {
        if (!memberships.some((membership) => membership.tenant_id === tenantId)) return;
        window.localStorage.setItem(ACTIVE_TENANT_KEY, tenantId);
        setActiveTenantId(tenantId);
      },
    }),
    [activeMembership, activeTenant, error, isLoading, memberships, profileName, tenants],
  );

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
}

